import { FieldInfo } from '@/components/FieldInfo';
import { studyQueryKeys, userQueryKeys } from '@/config/queryKeys';
import { apiClient } from '@/lib/api-client';
import { extractFieldErrors } from '@/lib/api-errors';
import { applyServerFieldErrors, clearServerFieldErrors } from '@/lib/form-errors';
import { mapFieldErrorsToForm } from '@/lib/map-field-errors';
import type { StudyCreate, StudyCreateResponse } from '@/types';
import { AutoComplete } from '@nautilusbio/nb-design-system';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Input, InputProps } from '@nautilusbio/nb-design-system/Input';
import { getStackClassName } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Textarea } from '@nautilusbio/nb-design-system/Textarea';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import { Loader, X } from 'lucide-react';
import React, { useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import createStyles from '../../../components/Create/styles.module.css';

import { nameSchema } from './create-form-utils';

const inputSharedProps: Partial<InputProps> = {
  autoComplete: 'off',
  className: createStyles.uiInput,
  size: 'xs',
};

const CreateForm: FC<{ onSuccess: () => void; userOrg: string }> = ({ onSuccess, userOrg }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedUsers, setSelectedUsers] = useState<Array<{ id: string; full_name: string }>>([]);
  const [userSearchValue, setUserSearchValue] = useState('');

  const { data: orgUsers = [] } = useQuery({
    queryKey: userQueryKeys.orgUserOptions(userOrg),
    queryFn: async () => {
      const result = await apiClient.get<Array<{ id: string; full_name: string; email: string }>>(
        userQueryKeys.endpoints.orgUsers(userOrg),
      );
      return result ?? [];
    },
    staleTime: 60_000,
  });

  const userOptions = useMemo(
    () =>
      orgUsers
        .filter(u => !selectedUsers.some(s => s.id === u.id))
        .map(u => ({ id: u.id, label: u.full_name, value: u.id })),
    [orgUsers, selectedUsers],
  );

  const addStudy = useMutation({
    mutationFn: async (newStudy: StudyCreate): Promise<StudyCreateResponse> => {
      const response = await apiClient.post<StudyCreateResponse>(studyQueryKeys.endpoints.list(userOrg), newStudy);

      if (!response) {
        throw new Error('Study creation failed - no response from server');
      }

      return response;
    },
    onError: error => {
      const fieldErrors = extractFieldErrors(error);
      const { formErrors, unmapped } = mapFieldErrorsToForm(fieldErrors, { arrayFields: [] });
      // Generic string `detail`/`message` responses land in `unmapped`. The deleted
      // remap-server-field-errors helper funneled these onto the name field so they stayed
      // visible; preserve that so a server message can't fail silently.
      if (unmapped.length > 0 && formErrors.name === undefined) {
        formErrors.name = unmapped[0];
      }
      applyServerFieldErrors(form, formErrors);
    },
    scope: {
      id: 'studies',
    },
  });

  const form = useForm({
    defaultValues: {
      description: '',
      name: '',
    },
    onSubmit: async ({ value }) => {
      // Clear previous server errors before submit using the public form API
      clearServerFieldErrors(form);

      const payload: StudyCreate = {
        name: value.name,
        description: value.description.trim() || null,
        allowed_user_ids: selectedUsers.map(u => u.id),
      };

      const studyPromise = addStudy.mutateAsync(payload);

      // Show loading/success/error toast purely for UI feedback
      toast.promise(studyPromise, {
        loading: t('study.creating', { ns: 'create' }),
        success: () => `${value.name} ${t('study.success', { ns: 'create' })}`,
        error: () => t('study.failed', { ns: 'create' }),
      });

      // Await the mutation so side effects only run after success.
      const result = await studyPromise;

      // Success: reset form, invalidate caches (via onSuccess), navigate
      form.reset();
      setSelectedUsers([]);
      setUserSearchValue('');
      onSuccess();
      navigate({ params: { studyId: result.id }, to: '/studies/$studyId' });
    },
  });

  return (
    <div className={createStyles.formContainer}>
      <form
        className={createStyles.form}
        onSubmit={async e => {
          e.preventDefault();
          e.stopPropagation();
          await form.handleSubmit();
        }}
      >
        <form.Field
          name="name"
          validators={{
            onChange: nameSchema,
            onMount: nameSchema,
          }}
        >
          {field => {
            const label = {
              children: t('general:name'),
              htmlFor: field.name,
              id: `label-${field.name}`,
            };

            return (
              <>
                <Input
                  {...inputSharedProps}
                  // oxlint-disable-next-line no-autofocus
                  autoFocus={true}
                  data-testid={`input-${field.name}`}
                  id={field.name}
                  label={label}
                  name={field.name}
                  onBlur={field.handleBlur}
                  onChange={e => field.handleChange(e.target.value)}
                  value={field.state.value}
                />

                <FieldInfo field={field} />
              </>
            );
          }}
        </form.Field>

        <form.Field name="description">
          {field => {
            const label = {
              children: t('general:desc'),
              htmlFor: field.name,
              id: `label-${field.name}`,
            };

            return (
              <>
                <Textarea
                  autoComplete="off"
                  className={createStyles.uiInput}
                  data-testid={`input-${field.name}`}
                  id={field.name}
                  label={label}
                  name={field.name}
                  onBlur={field.handleBlur}
                  onChange={({ target }: { target: { value: string } }) => field.handleChange(target.value)}
                  rows={5}
                  size="xs"
                  value={field.state.value}
                />

                <FieldInfo field={field} />
              </>
            );
          }}
        </form.Field>

        <AutoComplete
          data-testid="input-allowed-users"
          isClearable={false}
          label={{
            children: t('study.team_members', { ns: 'create' }),
            id: 'label-allowed-users',
          }}
          onInputChange={setUserSearchValue}
          onSelectionChange={key => {
            if (!key) return;
            const user = orgUsers.find(u => u.id === String(key));
            if (!user) return;
            setSelectedUsers(prev => [...prev, { id: user.id, full_name: user.full_name }]);
            setUserSearchValue('');
          }}
          options={userOptions}
          placeholder={t('study.add_member_placeholder', { ns: 'create' })}
          size={inputSharedProps.size}
          value={userSearchValue}
        />

        {selectedUsers.length > 0 && (
          <ul className={createStyles.selectedUsersList}>
            {selectedUsers.map(user => (
              <li
                className={clsx(
                  createStyles.selectedUserRow,
                  getStackClassName(['displayflex', 'aicenter', 'jcspace-between', 'horizontal', 'gap2']),
                )}
                key={user.id}
              >
                <Text as="span">{user.full_name}</Text>

                <Button
                  aria-label={t('study.remove_user', { ns: 'create', name: user.full_name })}
                  data-testid={`remove-user-${user.id}`}
                  onPress={() => setSelectedUsers(prev => prev.filter(u => u.id !== user.id))}
                  size="sm"
                  visual="unstyled"
                >
                  <X size={14} />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <form.Subscribe
          selector={state => ({
            canSubmit: state.canSubmit,
            name: state.values.name,
            isSubmitting: state.isSubmitting,
            isSubmitted: state.isSubmitted,
          })}
        >
          {({ name, canSubmit, isSubmitting, isSubmitted }) => {
            const hasRequiredValues = (name?.trim?.()?.length ?? 0) > 0;
            return (
              <div className={createStyles.footer}>
                <Button
                  className={clsx((isSubmitting || isSubmitted) && createStyles.noPointer)}
                  data-testid="submit-btn"
                  isDisabled={!hasRequiredValues || !canSubmit || isSubmitting}
                  size="xl"
                  type="submit"
                  visual={isSubmitting || isSubmitted ? 'success' : 'primary'}
                >
                  {isSubmitting || isSubmitted ? (
                    <>
                      <Loader className={createStyles.spinner} /> &nbsp;{t('general:submitting')}
                    </>
                  ) : (
                    t('general:submit')
                  )}
                </Button>
              </div>
            );
          }}
        </form.Subscribe>
      </form>
    </div>
  );
};

export default CreateForm;
